/**
 * FlatModifier (Foundry: rule-element/flat-modifier.ts): a constant bonus, penalty or modifier
 * to one or more selectors, registered as a deferred constructor so each statistic can resolve
 * the value with its own roll options.
 */
import { createModifier, testModifier, type Modifier } from "../modifiers";
import { cleanRuleValue, resolveInjectedProperties, resolveValue } from "../resolve";
import { isPhysicalType } from "../physical";
import type { RawPredicate } from "../predicate";
import type { EngineState } from "../state";
import type { DeferredValueParams } from "../synthetics";
import { ATTRIBUTE_LABELS, DAMAGE_CATEGORIES_UNIQUE, DAMAGE_TYPES, isAttribute, isModifierType, type Attribute, type ModifierType } from "../values";
import type { RuleHandler, RuleInstance } from "./base";

interface FlatModifierData {
  selectors: string[];
  type: ModifierType;
  ability: Attribute | null;
  value: unknown;
  min: number | undefined;
  max: number | undefined;
  force: boolean;
  hideIfDisabled: boolean;
  damageType: string | null;
  damageCategory: string | null;
  critical: boolean | null;
  tags: string[];
  battleForm: boolean;
  removeAfterRoll: unknown;
  /** Foundry's `fromEquipment` (true unless set false): an item bonus a physical Entry gives, which Automatic Bonus Progression suppresses (#12). */
  fromEquipment: boolean;
}

function parse(rule: RuleInstance): void {
  const s = rule.source;
  const rawSelector = s.selector;
  const selectors = (Array.isArray(rawSelector) ? rawSelector : [rawSelector]).filter((x): x is string => typeof x === "string" && x.length > 0);
  if (selectors.length === 0) return rule.fail("selector: must have at least 1");
  if (s.type !== undefined && !isModifierType(s.type)) return rule.fail(`type: "${String(s.type)}" is not a modifier type`);
  const type: ModifierType = isModifierType(s.type) ? s.type : "untyped";
  let ability: Attribute | null = null;
  if (s.ability !== undefined) {
    if (!isAttribute(s.ability)) return rule.fail(`ability: "${String(s.ability)}" is not an attribute`);
    ability = s.ability;
  }
  if (type === "ability" && !ability) return rule.fail("ability: must be defined");
  let value = cleanRuleValue(s.value);
  if (type !== "ability" && value === undefined) return rule.fail("value: may not be undefined");
  const force = s.force === true;
  if (force && type === "untyped") return rule.fail("type: may not be undefined when forced");
  for (const bound of ["min", "max"] as const) {
    if (s[bound] !== undefined && typeof s[bound] !== "number") return rule.fail(`${bound}: must be a number`);
  }
  if (s.damageCategory !== undefined && !(DAMAGE_CATEGORIES_UNIQUE as readonly string[]).includes(String(s.damageCategory))) {
    return rule.fail(`damageCategory: "${String(s.damageCategory)}" is not a damage category`);
  }
  if (s.removeAfterRoll !== undefined && rule.item.type !== "effect") return rule.fail("removeAfterRoll: may only be used with effects");
  if (type === "ability" && ability) {
    rule.slug = ability;
    rule.label = ATTRIBUTE_LABELS[ability];
    value = `@actor.abilities.${ability}.mod`;
  }
  const critical = typeof s.critical === "boolean" && selectors.some((sel) => sel.includes("damage")) ? s.critical : null;
  const data: FlatModifierData = {
    selectors,
    type,
    ability,
    value,
    min: typeof s.min === "number" ? s.min : undefined,
    max: typeof s.max === "number" ? s.max : undefined,
    force,
    hideIfDisabled: s.hideIfDisabled === true,
    damageType: typeof s.damageType === "string" && s.damageType ? s.damageType : null,
    damageCategory: typeof s.damageCategory === "string" ? s.damageCategory : null,
    critical,
    tags: Array.isArray(s.tags) ? s.tags.filter((t): t is string => typeof t === "string") : [],
    battleForm: s.battleForm === true,
    removeAfterRoll: s.removeAfterRoll,
    // Forced false when the Rule Element cannot be relevant to ABP (Foundry does the same).
    fromEquipment: s.fromEquipment !== false && type === "item" && isPhysicalType(rule.item.type),
  };
  rule.data = data as unknown as Record<string, unknown>;
}

function beforePrepareData(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored) return;
  const data = rule.data as unknown as FlatModifierData;
  const label = rule.reducedLabel;
  const slug = rule.slug ?? label;
  const selectors = data.selectors.map((s) => resolveInjectedProperties(rule, state, s)).filter((s) => !!s);
  for (const selector of selectors) {
    if (selector === "null") continue;
    const construct = (params: DeferredValueParams = {}): Modifier | null => {
      const resolved = Number(resolveValue(rule, state, data.value, 0, { resolvables: params.resolvables ?? {} })) || 0;
      if (rule.ignored) return null;
      const finalValue = Math.min(Math.max(resolved, data.min ?? resolved), data.max ?? resolved);
      // Automatic Bonus Progression (#12): item bonuses from equipment give way to the potencies (Foundry: ABP.suppressRuleElement).
      if (state.variantRules.automaticBonusProgression && data.type === "item" && finalValue >= 0 && data.fromEquipment) return null;
      const damageType = data.damageType ? resolveInjectedProperties(rule, state, data.damageType, { warn: false }) || null : null;
      if (damageType !== null && !(DAMAGE_TYPES as readonly string[]).includes(damageType)) {
        if (rule.test(state, params.test ?? new Set())) rule.fail(`damageType: "${damageType}" is unrecognized`);
        return null;
      }
      const predicate = resolveInjectedProperties(rule, state, structuredClone(rule.predicate));
      if (data.battleForm && !predicate.includes("battle-form")) predicate.push("battle-form");
      const modifier = createModifier({
        slug,
        label,
        value: finalValue,
        type: data.type,
        ability: data.type === "ability" ? data.ability : null,
        predicate,
        parentOptions: rule.item.getRollOptions("parent"),
        force: data.force,
        damageType,
        damageCategory: data.damageCategory,
        critical: data.critical,
        tags: data.tags,
        hideIfDisabled: data.hideIfDisabled,
        source: rule.item.uuid,
        sourceName: rule.item.name,
        removeAfterRoll: data.removeAfterRoll === true ? true : data.removeAfterRoll === "if-enabled" ? "if-enabled" : Array.isArray(data.removeAfterRoll) ? (data.removeAfterRoll as RawPredicate) : false,
      });
      if (params.test) testModifier(modifier, params.test);
      return modifier;
    };
    (state.synthetics.modifiers[selector] ??= []).push(construct);
  }
}

export const FlatModifierHandler: RuleHandler = { key: "FlatModifier", parse, beforePrepareData };
